import React from 'react'
import { Link } from 'react-router-dom'
import BookResource from './resource'
import BookText from './text'
import { ChapterLink, ChapterLinkScenario } from './link'

const TYPE_EG = {
  discovery: 'Découverte',
  adventure: 'Aventure',
  immersion: 'Immersion',
  investigation: 'Investigation',
  box: 'Box',
  info: 'Info',
  other: 'Autre'
}
const PUBLIC_TARGET = { children: 'Enfants', adults: 'Ados/adultes', families: 'Familles' }
const ORIENTATION = { portrait: 'Portrait', landscape: 'Paysage' }
const TYPE_STRUCTURE = {
  artisan: 'Artisan, ferme',
  base_loisir: 'Base de loisir',
  chateau: 'Château',
  collectivite: 'Collectivité',
  espace_naturel: 'Espace naturel',
  grottes: 'Grottes',
  hebergement: 'Hébergement',
  montagne: 'Montagne',
  musee: 'Musée',
  office_tourisme: 'Office de tourisme',
  parc_accrobranche: 'Parc accrobranche',
  parc_animalier_botanique: 'Parc animalier et botanique',
  prescripteur: 'Prescripteur',
  prive: 'Privé',
  public: 'Public'
}

const Field = ({ label, children }) => (
  <div className="field">
    <div className="field-label">{label}</div>
    <div className="field-value">{children}</div>
  </div>
)

export default ({ experience, chapter, chapters, timeLimit, score, url, highlights }) => {
  const trackPage = chapters[experience.startTrack] || {}
  const { onStart, theme, tags } = experience
  const actionScenarios = (onStart || []).filter(a => a.type === 'load_scenario')

  return (
    <article className="experience">
      <h1>
        <BookText
          text={experience.name}
          collection="experiences"
          id={experience._id}
          path="name"
          highlights={highlights}
          style={{ display: 'inline-block' }}
        />
        <Link to={url}>
          <i className="fa fa-edit" />
        </Link>
      </h1>
      <section>
        <aside className="common">
          {(timeLimit > 0 || score > 0) && (
            <div className="aside-metrics">
              {timeLimit > 0 && (
                <span className="aside-metric">
                  <i className="fa fa-clock-o" /> {timeLimit} s
                </span>
              )}
              {score > 0 && (
                <span className="aside-metric">
                  <i className="fa fa-trophy" /> {score} pts
                </span>
              )}
            </div>
          )}
          {experience.difficulty ? (
            <Field label="Difficulté">
              {{ 1: 'Facile', 2: 'Intermédiaire', 3: 'Difficile' }[experience.difficulty]}
            </Field>
          ) : null}
          {experience.duration ? <Field label="Durée">{experience.duration} min</Field> : null}
          {experience.length ? <Field label="Longueur">{experience.length} km</Field> : null}
          {experience.elevationGain ? <Field label="Dénivelé positif">{experience.elevationGain} m</Field> : null}
          {experience.elevationLoss ? <Field label="Dénivelé négatif">{experience.elevationLoss} m</Field> : null}
          {experience.locale && <Field label="Langue">{experience.locale}</Field>}
          {experience.typeEG && <Field label="Type d'Explor Games">{TYPE_EG[experience.typeEG] || experience.typeEG}</Field>}
          {experience.publicTarget && (
            <Field label="Public cible">{PUBLIC_TARGET[experience.publicTarget] || experience.publicTarget}</Field>
          )}
          {experience.typeStructure && experience.typeStructure.length > 0 && (
            <Field label="Type de structure">{experience.typeStructure.map(s => TYPE_STRUCTURE[s] || s).join(', ')}</Field>
          )}
          {experience.orientation && (
            <Field label="Orientation">{ORIENTATION[experience.orientation] || experience.orientation}</Field>
          )}
          {experience.clientName && (
            <Field label="Client">
              {experience.clientName}
              {experience.clientSite && (
                <>
                  {' — '}
                  <a href={experience.clientSite} target="_blank" rel="noopener noreferrer">
                    {experience.clientSite}
                  </a>
                </>
              )}
            </Field>
          )}
          {tags && tags.length > 0 && (
            <Field label="Mots clés">
              <div className="aside-tags">
                {tags.map((tag, index) => (
                  <span className="aside-tag" key={index}>
                    {tag}
                  </span>
                ))}
              </div>
            </Field>
          )}
          <Field label="Sauvegarde de la progression">{experience.useProgression ? 'Oui' : 'Non'}</Field>
          <Field label="Attendre la fin du téléchargement">{experience.waitForDownload ? 'Oui' : 'Non'}</Field>
        </aside>
        <div>
          <BookResource id={experience.picture} path="picture" highlights={highlights} />
          <BookText
            collection="experiences"
            id={experience._id}
            path="description"
            text={experience.description}
            highlights={highlights}
          />

          {experience.splashscreen && (
            <div>
              <h4>Splashscreen</h4>
              <BookResource
                id={experience.splashscreen}
                path="splashscreen"
                highlights={highlights}
                style={{ maxWidth: '200px' }}
              />
            </div>
          )}
          {experience.videoExp && experience.videoExp.videoUri && (
            <p>
              Vidéo :{' '}
              <a href={experience.videoExp.videoUri} target="_blank" rel="noopener noreferrer">
                {experience.videoExp.videoUri}
              </a>
            </p>
          )}

          {experience.characters && experience.characters.length > 0 && (
            <div>
              <h4>
                Personnages{' '}
                <Link to={url + '/characters'}>
                  <i className="fa fa-edit" />
                </Link>
              </h4>
              <div className="characters">
                {experience.characters.map(({ label, image, _id, variants }, index) => {
                  return (
                    <>
                      <div key={_id}>
                        <BookResource id={image} title={label} path={'characters°|°' + index} highlights={highlights} />
                      </div>
                      {(variants || []).map(({ label: variantName, image, _id }, index) => {
                        return (
                          <div key={_id}>
                            <BookResource
                              id={image}
                              title={label + ' ' + variantName}
                              path={'characters°|°' + index + '°|°variants°|°' + index}
                              highlights={highlights}
                            />
                          </div>
                        )
                      })}
                    </>
                  )
                })}
              </div>
            </div>
          )}
          <h4>
            Thème
            <Link to={url + '/theme'}>
              <i className="fa fa-edit" />
            </Link>
          </h4>
          <div className="theme">
            {theme &&
              theme.assets &&
              Object.keys(theme.assets)
                .map(key => {
                  return { key, asset: theme.assets[key] }
                })
                .map(({ key, asset }) => (
                  <div key={key}>
                    <BookResource
                      id={asset}
                      title={key.split('_').join(' ')}
                      path={'theme°|°assets°|°' + key}
                      highlights={highlights}
                    />
                  </div>
                ))}
          </div>
          {actionScenarios.length > 0 && <ChapterLinkScenario scenarioId={actionScenarios[0].scenario} chapters={chapters} />}
          {actionScenarios.length === 0 && <ChapterLink chapter={trackPage.chapter} height="200px" />}
        </div>
      </section>
    </article>
  )
}
